/**
 * Full notification management panel — used in both Enhancer and Center Manager dashboards.
 * Reads from GET /notifications (personal notifications feed, newest first).
 * Features: mark read, mark all read, delete, navigate/deep-link, inline reply for messages.
 */
import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, Trash2, MessageSquare, Calendar, DollarSign, Loader2, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// ─── Type metadata ────────────────────────────────────────────────────────────
const TYPE_META = {
  message:      { label: "Message",     Icon: MessageSquare, bg: "bg-[#144F75]/10",  color: "text-[#144F75]" },
  inquiry:      { label: "Inquiry",     Icon: MessageSquare, bg: "bg-[#144F75]/10",  color: "text-[#144F75]" },
  appointment:  { label: "Booking",     Icon: Calendar,      bg: "bg-[#5C8A72]/10",  color: "text-[#5C8A72]" },
  booking:      { label: "Booking",     Icon: Calendar,      bg: "bg-[#5C8A72]/10",  color: "text-[#5C8A72]" },
  reschedule:   { label: "Reschedule",  Icon: Calendar,      bg: "bg-[#D98E73]/15",  color: "text-[#B5794E]" },
  cancellation: { label: "Cancelled",   Icon: Calendar,      bg: "bg-[#B45151]/10",  color: "text-[#B45151]" },
  payout:       { label: "Payout",      Icon: DollarSign,    bg: "bg-[#5C8A72]/10",  color: "text-[#5C8A72]" },
  payment:      { label: "Payment",     Icon: DollarSign,    bg: "bg-[#5C8A72]/10",  color: "text-[#5C8A72]" },
  earnings:     { label: "Earnings",    Icon: DollarSign,    bg: "bg-[#5C8A72]/10",  color: "text-[#5C8A72]" },
  system:       { label: "System",      Icon: Info,          bg: "bg-[#F1EEE8]",     color: "text-[#8FA89B]"  },
  digest:       { label: "Digest",      Icon: Info,          bg: "bg-[#F1EEE8]",     color: "text-[#8FA89B]"  },
};
const DEFAULT_META = { label: "Notification", Icon: Bell, bg: "bg-[#F1EEE8]", color: "text-[#8FA89B]" };

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const m = Math.floor((Date.now() - d.getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}

/** Resolve the best navigation target for a notification, client-side. */
function resolveLink(n, userRole) {
  const stored = n.link || "";
  const isMessage = ["message", "inquiry"].includes(n.type);
  const isBooking = ["appointment", "booking", "reschedule", "cancellation"].includes(n.type);
  const isPayout  = ["payout", "payment", "earnings"].includes(n.type);

  if (isMessage) {
    // Prefer thread_id-based resolution (most reliable)
    if (n.sender_id) return `/messages?to=${n.sender_id}`;
    if (stored && stored.startsWith("/messages")) return stored;
    return "/messages";
  }
  if (isPayout) return "/enhancer?tab=Earnings";
  if (isBooking) {
    if (stored && !["", "/admin", "/dashboard"].includes(stored) && !stored.match(/^\/admin(\?|\/|$)/)) return stored;
    return userRole === "location_manager" ? "/manager?tab=Appointments" : "/enhancer?tab=Bookings";
  }
  // system/digest/order — trust stored link unless it's /admin (for non-admins)
  if (stored && stored !== "" && stored !== "/dashboard" && stored !== "/admin") return stored;
  return null; // no navigation — notification is info-only
}

function isMessageType(n) {
  return ["message", "inquiry"].includes(n.type);
}

// ─── Individual row ───────────────────────────────────────────────────────────
function NotifRow({ n, onMarkRead, onDelete }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const meta = TYPE_META[n.type] || DEFAULT_META;
  const { Icon } = meta;
  const link = resolveLink(n, user?.role);
  const isMsg = isMessageType(n);

  const handleClick = () => {
    if (!n.read) onMarkRead(n.id);
    if (isMsg) {
      // Resolve the conversation partner and navigate to the Messages page.
      const tid = n.thread_id || "";
      if (tid && tid !== "__unresolvable__") {
        const parts = tid.split(":");
        const otherId = parts.find((p) => p && p !== (user?.id || ""));
        if (otherId) { nav(`/messages?to=${otherId}`); return; }
      }
      if (n.sender_id && n.sender_id !== (user?.id || "")) { nav(`/messages?to=${n.sender_id}`); return; }
      const m = /^\/messages\?to=([A-Za-z0-9]+)/.exec(n.link || "");
      if (m && m[1] !== (user?.id || "")) { nav(`/messages?to=${m[1]}`); return; }
      // Guest inquiry (no sender account): use stored dashboard link
      if (n.type === "inquiry" && n.link && n.link !== "/messages") { nav(n.link); return; }
      nav("/messages"); return;
    }
    if (link) nav(link);
  };

  return (
    <div
      data-testid={`notif-row-${n.id}`}
      className={`group flex items-start gap-3 p-4 transition-colors ${n.read ? "" : "bg-[#FBF9F5]"} ${(link || isMsg) ? "cursor-pointer hover:bg-[#F4F1EA]" : ""}`}
      onClick={handleClick}
    >
      {/* Icon */}
      <div className={`shrink-0 h-9 w-9 rounded-full grid place-items-center ${meta.bg}`}>
        <Icon size={16} className={meta.color} />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.color} font-medium`}>{meta.label}</span>
          {n.actor_name && <span className="text-xs text-[#8FA89B]">from {n.actor_name}</span>}
          {!n.read && <span className="h-2 w-2 rounded-full bg-[#D98E73] shrink-0" data-testid={`notif-unread-dot-${n.id}`} />}
        </div>
        <p className="text-sm text-[#1A242B] mt-0.5 font-medium leading-snug">{n.text}</p>
        <p className="text-[11px] text-[#8FA89B] mt-1">{timeAgo(n.created_at)}</p>
      </div>

      {/* Actions */}
      <div className="shrink-0 flex items-center gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        {isMsg && (
          <button
            onClick={() => { if (!n.read) onMarkRead(n.id); handleClick(); }}
            className="p-1.5 rounded-lg hover:bg-[#144F75]/10 text-[#144F75]"
            title="View conversation"
            data-testid={`notif-reply-${n.id}`}
          ><ArrowRight size={13} /></button>
        )}
        {(link && !isMsg) && (
          <button
            onClick={() => { if (!n.read) onMarkRead(n.id); nav(link); }}
            className="p-1.5 rounded-lg hover:bg-[#144F75]/10 text-[#144F75]"
            title="Go to"
            data-testid={`notif-goto-${n.id}`}
          ><ArrowRight size={13} /></button>
        )}
        {!n.read && (
          <button
            onClick={() => onMarkRead(n.id)}
            className="p-1.5 rounded-lg hover:bg-[#5C8A72]/10 text-[#5C8A72]"
            title="Mark read"
            data-testid={`notif-read-${n.id}`}
          ><CheckCheck size={13} /></button>
        )}
        <button
          onClick={() => onDelete(n.id)}
          className="p-1.5 rounded-lg hover:bg-[#B45151]/10 text-[#B45151]"
          title="Delete"
          data-testid={`notif-delete-${n.id}`}
        ><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────
export default function NotificationsPanel() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all"); // all | unread | messages | bookings

  const { data, isLoading } = useQuery({
    queryKey: ["notifications-panel"],
    queryFn: () => api.get("/notifications", { params: { limit: 150 } }).then((r) => r.data),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["notifications-panel"] });
    qc.invalidateQueries({ queryKey: ["notifications"] }); // bell badge
  }, [qc]);

  const markReadMut = useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/read`).then((r) => r.data),
    onSuccess: invalidate,
    onError: () => toast.error("Couldn't mark as read"),
  });

  const markAllMut = useMutation({
    mutationFn: () => api.post("/notifications/read-all").then((r) => r.data),
    onSuccess: () => { toast.success("All notifications marked read"); invalidate(); },
    onError: () => toast.error("Couldn't mark all as read"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/notifications/${id}`).then((r) => r.data),
    onSuccess: () => { invalidate(); toast.success("Notification deleted"); },
    onError: () => toast.error("Couldn't delete notification"),
  });

  const allNotifs = data?.items || [];
  const unreadCount = allNotifs.filter((n) => !n.read).length;

  const filtered = allNotifs.filter((n) => {
    if (filter === "unread") return !n.read;
    if (filter === "messages") return ["message", "inquiry"].includes(n.type);
    if (filter === "bookings") return ["appointment", "booking", "reschedule", "cancellation"].includes(n.type);
    return true;
  });

  return (
    <div data-testid="notifications-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2">
            <Bell size={20} className="text-[#144F75]" /> Notifications
            {unreadCount > 0 && (
              <span className="text-sm bg-[#D98E73] text-white rounded-full px-2 py-0.5 font-medium" data-testid="notif-panel-unread-count">
                {unreadCount}
              </span>
            )}
          </h2>
          <p className="text-sm text-[#4A5D6B] mt-0.5">Your messages, bookings, payouts, and alerts — all in one place.</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllMut.mutate()}
            disabled={markAllMut.isPending}
            data-testid="notif-panel-read-all"
            className="eyw-btn-ghost !py-2 text-sm flex items-center gap-1.5"
          >
            {markAllMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCheck size={13} />}
            Mark all read
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-[#E8E3DC] pb-2" data-testid="notif-filter-tabs">
        {[
          { key: "all",      label: "All" },
          { key: "unread",   label: `Unread${unreadCount > 0 ? ` (${unreadCount})` : ""}` },
          { key: "messages", label: "Messages" },
          { key: "bookings", label: "Bookings" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`notif-filter-${key}`}
            className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${filter === key ? "bg-[#144F75] text-white" : "text-[#4A5D6B] hover:bg-[#F1EEE8]"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="eyw-card divide-y divide-[#E8E3DC] overflow-hidden" data-testid="notif-panel-list">
        {isLoading ? (
          <div className="p-10 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#8FA89B] text-center p-10" data-testid="notif-panel-empty">
            {filter === "all" ? "No notifications yet." : `No ${filter} notifications.`}
          </p>
        ) : (
          filtered.map((n) => (
            <NotifRow
              key={n.id}
              n={n}
              onMarkRead={(id) => markReadMut.mutate(id)}
              onDelete={(id) => deleteMut.mutate(id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
